import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { bezuege, nutztFunktion } from "@/office/excel/formeln";
import { formulaCells, hasFormula, isNumber, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionZielwertsuche: Lesson = {
  id: "zielwertsuche",
  title: "Die Zielwertsuche",
  summary: "Rückwärts rechnen: Welcher Kreditbetrag passt zu 500 € Monatsrate?",
  durationMinutes: 10,
  learningGoals: [
    "Ein kleines Rechenmodell mit Eingabezellen und einer Ergebnisformel aufbauen",
    "Mit der Zielwertsuche einen Eingabewert für ein gewünschtes Ergebnis ermitteln",
  ],
  steps: [
    {
      id: "modell",
      title: "Das Modell: Eingaben und eine Formel",
      explanation: [
        "Jede Was-wäre-wenn-Analyse braucht ein sauberes Modell: Eingabewerte in eigenen " +
          "Zellen, eine Formel, die daraus das Ergebnis berechnet – genau die Regel aus dem " +
          "Modul „Formeln und Bezüge“, keine getippten Zahlen in der Formel.",
        "Ein Beispiel: Kreditbetrag in B1 (20000), Jahreszins in B2 (4 %), Laufzeit in " +
          "Monaten in B3 (48). Die Monatsrate berechnet RMZ: =RMZ(B2/12;B3;-B1). Das Minus " +
          "vor B1 sorgt dafür, dass die Rate positiv erscheint.",
      ],
      keyPoints: [
        "Eingaben in eigene Zellen, Ergebnis per Formel.",
        "=RMZ(Zins/12;Monate;-Betrag) = Monatsrate.",
      ],
    },
    {
      id: "zielwert",
      title: "Die Zielwertsuche",
      explanation: [
        "Die umgekehrte Frage – welcher Kreditbetrag ergibt genau 500 € Rate? – beantwortet " +
          "die Zielwertsuche: „Daten“ → „Was-wäre-wenn-Analyse“ → „Zielwertsuche“. " +
          "„Zielzelle“ ist die Formel (B4), „Zielwert“ 500, „Veränderbare Zelle“ die " +
          "Eingabe (B1). Excel probiert so lange Werte aus, bis die Formel passt.",
        "Das Ergebnis überschreibt die Eingabezelle. Wer den alten Wert behalten will, " +
          "notiert ihn vorher oder verwendet Szenarien (nächste Lektion). Die Zielwertsuche " +
          "verändert genau eine Zelle; für mehrere Stellschrauben mit Nebenbedingungen " +
          "gibt es den Solver.",
      ],
      keyPoints: [
        "Daten → Was-wäre-wenn-Analyse → Zielwertsuche.",
        "Zielzelle = Formel, veränderbare Zelle = Eingabe ohne Formel.",
        "Überschreibt die Eingabe – vorher sichern, wenn nötig.",
      ],
      task: {
        instruction:
          "Baue das Kreditmodell mit RMZ nach und ermittle mit der Zielwertsuche den " +
          "Kreditbetrag, bei dem die Monatsrate genau 500 € beträgt.",
        hint: "Zielzelle: die RMZ-Formel, Zielwert: 500, veränderbare Zelle: der Kreditbetrag.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar("Die Formeln der Zellen", "1.4", "Prüfe selbst, ob die Rate 500 € zeigt.", true);
          }
          const raten = formulaCells(mappe).filter((zelle) => nutztFunktion(zelle.formula, "rmz"));
          if (raten.length === 0) {
            return fail("Es gibt noch keine Formel mit RMZ.", "=RMZ(B2/12;B3;-B1) in B4.");
          }
          const treffer = raten.find((zelle) => isNumber(zelle) && Math.abs(Math.abs(Number(zelle.value)) - 500) < 0.01);
          const blatt = mappe.sheets.find((sheet) => treffer !== undefined && sheet.cells.includes(treffer));
          const eingabenFest =
            treffer !== undefined &&
            bezuege(treffer.formula)
              .filter((bezug) => bezug.sheet === null)
              .every((bezug) => {
                const adresse = bezug.text.replace(/\$/g, "");
                const zelle = blatt?.cells.find((kandidat) => kandidat.address === adresse);
                return !zelle || !hasFormula(zelle);
              });
          return fromCriteria(
            [
              criterion(`Eine RMZ-Formel ergibt 500 (gefunden: ${raten.map((zelle) => zelle.text).join(", ")})`, treffer !== undefined),
              criterion("Die Eingaben der Formel sind feste Werte, keine Formeln", eingabenFest),
            ],
            {
              success: "Rückwärts gerechnet – Excel hat den passenden Kreditbetrag gefunden.",
              failure: "Die Rate liegt noch nicht bei 500 €.",
              hint: "Die veränderbare Zelle muss eine Eingabe sein, auf die die RMZ-Formel verweist.",
            },
          );
        },
      },
    },
  ],
};
